import { useCallback, useEffect, useRef } from 'react';

import { TEAM_CHAT_TYPING_DEBOUNCE_MS } from 'constants/teamChat';
import useDebounce from 'hooks/useDebounce';

const useChatTyping = ({ onStartTyping = null, onStopTyping = null }) => {
  const isTypingRef = useRef(false);
  const onStopTypingRef = useRef(onStopTyping);

  const stopTypingDebounced = useDebounce(() => {
    if (!isTypingRef.current) {
      return;
    }

    isTypingRef.current = false;
    onStopTypingRef.current?.();
  }, TEAM_CHAT_TYPING_DEBOUNCE_MS);

  const flushStopTyping = useCallback(() => {
    stopTypingDebounced.cancel();

    if (!isTypingRef.current) {
      return;
    }

    isTypingRef.current = false;
    onStopTypingRef.current?.();
  }, [stopTypingDebounced]);

  const notifyTyping = useCallback(
    value => {
      if (!onStartTyping) {
        return;
      }

      if (!value) {
        flushStopTyping();

        return;
      }

      if (!isTypingRef.current) {
        isTypingRef.current = true;
        onStartTyping();
      }

      stopTypingDebounced();
    },
    [onStartTyping, stopTypingDebounced, flushStopTyping],
  );

  useEffect(() => {
    onStopTypingRef.current = onStopTyping;
  }, [onStopTyping]);

  useEffect(() => () => flushStopTyping(), [flushStopTyping]);

  return { notifyTyping, flushStopTyping };
};

export default useChatTyping;
